import { Target, Dumbbell, Zap, Trophy, Flame, Crown } from "lucide-react";

export const BETA_MODE = false;

export const PHASES = [
  { week: [1, 4], name: "Structural Integrity", focus: "Build foundation", color: "blue", icon: Target, forBeginners: true },
  { week: [5, 8], name: "Hypertrophy", focus: "Build muscle mass", color: "purple", icon: Dumbbell },
  { week: [9, 12], name: "Explosive Power", focus: "Convert to power", color: "orange", icon: Zap },
  { week: [13, 16], name: "Game-Specific", focus: "Peak performance", color: "green", icon: Trophy },
  { week: [17, 19], name: "Elite Conditioning", focus: "Push beyond limits", color: "red", icon: Flame },
  { week: [20, 23], name: "Championship Ready", focus: "Dominate the competition", color: "yellow", icon: Crown },
];

export const SKILL_LEVELS = {
  beginner: { startWeek: 1, totalWeeks: 23, totalDays: 161, label: "Beginner", description: "New to structured training" },
  intermediate: { startWeek: 1, totalWeeks: 23, totalDays: 161, label: "Intermediate", description: "Some training experience" },
  advanced: { startWeek: 5, totalWeeks: 19, totalDays: 133, label: "Advanced", description: "Experienced athlete - skip foundation" },
};

export const MOTIVATIONAL_QUOTES = [
  { quote: "Train when no one's watching. Win when everyone is.", author: "Pivot" },
  { quote: "Comfort is the enemy of progress.", author: "Pivot" },
  { quote: "The work you put in today is the version of you they'll see tomorrow.", author: "Pivot" },
  { quote: "Discipline outlasts motivation every time.", author: "Pivot" },
  { quote: "Sweat now, dominate later.", author: "Pivot" },
  { quote: "Your only competition is the version of you from yesterday.", author: "Pivot" },
  { quote: "Excuses don't burn calories.", author: "Pivot" },
  { quote: "The grind isn't punishment. It's the price.", author: "Pivot" },
  { quote: "Show up. Lock in. Repeat.", author: "Pivot" },
  { quote: "Pressure makes diamonds. Pressure breaks pretenders.", author: "Pivot" },
  { quote: "Skill is built in the silence between reps.", author: "Pivot" },
  { quote: "Don't count the days. Make the days count.", author: "Pivot" },
  { quote: "Train like your spot's not safe — because it isn't.", author: "Pivot" },
  { quote: "Greatness leaves no shortcuts. Take the long road.", author: "Pivot" },
  { quote: "Stop scrolling. Start grinding.", author: "Pivot" },
  { quote: "The court rewards consistency over talent.", author: "Pivot" },
  { quote: "Today's pain is tomorrow's edge.", author: "Pivot" },
  { quote: "Hard work doesn't get tired. It just gets sharper.", author: "Pivot" },
  { quote: "If it doesn't challenge you, it doesn't change you.", author: "Pivot" },
  { quote: "Step on the court already won.", author: "Pivot" },
];

export const DAILY_CHALLENGES = [
  { id: 1, name: "Early Bird", description: "Complete workout before 8 AM", points: 50, icon: "🌅" },
  { id: 2, name: "No Rest", description: "Finish all sets without extended rest", points: 75, icon: "⚡" },
  { id: 3, name: "Double Up", description: "Complete 2 extra sets today", points: 100, icon: "💪" },
  { id: 4, name: "Perfect Form", description: "Record yourself and verify form", points: 50, icon: "🎯" },
  { id: 5, name: "Hydration Hero", description: "Drink 1 gallon of water today", points: 25, icon: "💧" },
  { id: 6, name: "Stretch Master", description: "15 min stretching post-workout", points: 30, icon: "🧘" },
  { id: 7, name: "Beast Mode", description: "Add 20% more reps to each exercise", points: 150, icon: "🔥" },
];

export const ELITE_BEAST_CHALLENGES = [
  { id: 101, name: "Inferno Protocol", description: "+50% reps with only 15 sec rest between sets", points: 500, icon: "🔥", multiplier: 1.5, restReduction: 0.25, difficulty: "EXTREME" },
  { id: 102, name: "Iron Lungs", description: "Complete entire workout without stopping for water", points: 400, icon: "🫁", multiplier: 1.0, noBreaks: true, difficulty: "HARD" },
  { id: 103, name: "Midnight Grinder", description: "Complete workout between 10 PM - 5 AM", points: 350, icon: "🌙", multiplier: 1.2, timeWindow: { start: 22, end: 5 }, difficulty: "HARD" },
  { id: 104, name: "Chain Breaker", description: "3 different workouts in 24 hours", points: 600, icon: "⛓️", workoutsRequired: 3, difficulty: "EXTREME" },
  { id: 105, name: "Titanium Will", description: "+30% intensity for 7 consecutive days", points: 1000, icon: "💎", multiplier: 1.3, streakRequired: 7, difficulty: "LEGENDARY" },
  { id: 106, name: "Gauntlet Run", description: "Complete all 3 drill categories in one session", points: 450, icon: "🏃", allCategories: true, difficulty: "HARD" },
  { id: 107, name: "Silent Assassin", description: "Complete workout with zero audio cues (pure focus)", points: 300, icon: "🤫", noAudio: true, difficulty: "MEDIUM" },
  { id: 108, name: "Double or Nothing", description: "Double all sets AND reps for one exercise", points: 550, icon: "🎰", multiplier: 2.0, singleExercise: true, difficulty: "EXTREME" },
  { id: 109, name: "Speed Demon", description: "Complete workout 40% faster than recommended", points: 400, icon: "⚡", timeMultiplier: 0.6, difficulty: "HARD" },
  { id: 110, name: "Apex Predator", description: "Top the leaderboard for any category this week", points: 750, icon: "👑", leaderboardRequired: true, difficulty: "LEGENDARY" },
];

export const TEAM_CHALLENGES = [
  { id: 1, name: "Squad Goals", description: "Team completes 500 total reps", target: 500, reward: "Squad Badge", icon: "👥" },
  { id: 2, name: "Mile Markers", description: "Team runs 100 miles combined", target: 100, reward: "Runner Badge", icon: "🏃" },
  { id: 3, name: "Iron Will", description: "7-day streak for all team members", target: 7, reward: "Iron Badge", icon: "🏆" },
];

export const getWeeklyEliteChallenge = () => {
  const weekOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 1)) / 604800000);
  return ELITE_BEAST_CHALLENGES[weekOfYear % ELITE_BEAST_CHALLENGES.length];
};
